/*
 * custom-pill-header.js
 * ---------------------------------------------------------------------------
 * Desktop header behaviour:
 *   - OPEN at the top of the page: full-width, transparent, on the page grid.
 *     On the homepage it overlays the hero (no reserved space), with light text.
 *   - On scroll it animates into a contained floating glass PILL, pinned near
 *     the top, with its colour adapting to the section behind it.
 *
 * Scoped to desktop (>= 990px). Below that it disables itself and restores
 * Horizon's default header. Self-contained — removing the
 * {% render 'custom-pill-header' %} line reverts the theme completely.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var DESKTOP_MIN = 990; // px — desktop breakpoint
  var PINNED_GAP = 12; // px — gap from the top once pinned
  var SHRINK_AT = 20; // px scrolled before collapsing into the pill

  var mql = window.matchMedia('(min-width: ' + DESKTOP_MIN + 'px)');

  var header, headerSection, announcement, columns, spacer, pageWrapper;
  var enabled = false;
  var overlay = false; // homepage: overlay the hero instead of reserving space
  var rafId = null;
  var lastTone = 'light'; // remembered tone for hysteresis / fallback

  function resolveNodes() {
    header = document.querySelector('#header-component');
    if (!header) return false;
    headerSection = header.closest('.header-section') || header.parentElement;
    columns = header.querySelector('.header__row--top .header__columns');
    announcement = headerSection ? headerSection.previousElementSibling : null;
    pageWrapper = document.querySelector('.page-wrapper');
    return !!(header && headerSection && columns);
  }

  // Overlay the hero only on templates designed for it (homepage).
  function isOverlayTemplate() {
    var main = document.querySelector('#MainContent');
    return !!(main && main.dataset && main.dataset.template === 'index');
  }

  function getScrollTop() {
    return Math.max(
      window.pageYOffset || 0,
      document.documentElement ? document.documentElement.scrollTop || 0 : 0,
      pageWrapper ? pageWrapper.scrollTop || 0 : 0
    );
  }

  /* ---- colour helpers ---------------------------------------------------- */

  var SAMPLE = 6; // px — tiny canvas edge for averaging behind-header pixels
  var imgCache = {}; // src -> { img, ready, failed } for CORS pixel sampling
  var sampleCanvas = null;
  var sampleCtx = null;

  function parseRGB(str) {
    if (!str) return null;
    var m = str.match(/rgba?\(([^)]+)\)/i);
    if (!m) return null;
    var parts = m[1].split(',').map(function (s) {
      return parseFloat(s.trim());
    });
    if (parts.length < 3 || isNaN(parts[0])) return null;
    var alpha = parts.length > 3 ? parts[3] : 1;
    return { r: parts[0], g: parts[1], b: parts[2], a: isNaN(alpha) ? 1 : alpha };
  }

  function relativeLuminance(r, g, b) {
    var channels = [r, g, b].map(function (v) {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  }

  function ensureCanvas() {
    if (sampleCanvas) return;
    sampleCanvas = document.createElement('canvas');
    sampleCanvas.width = SAMPLE;
    sampleCanvas.height = SAMPLE;
    sampleCtx = sampleCanvas.getContext('2d', { willReadFrequently: true });
  }

  // Reload the image cross-origin so its pixels are readable from a canvas.
  // The browser keys its cache by CORS mode, so this is (almost) free after
  // the first paint. Returns a cache entry; re-runs update() once it loads.
  function getCorsImage(src) {
    if (!src) return null;
    if (imgCache[src]) return imgCache[src];
    var entry = { img: new Image(), ready: false, failed: false };
    entry.img.crossOrigin = 'anonymous';
    entry.img.onload = function () {
      entry.ready = true;
      requestUpdate();
    };
    entry.img.onerror = function () {
      entry.failed = true;
    };
    entry.img.src = src;
    imgCache[src] = entry;
    return entry;
  }

  function extractUrl(bg) {
    var m = bg.match(/url\((['"]?)(.*?)\1\)/i);
    return m ? m[2] : null;
  }

  // Average luminance of the part of a loaded image overlapping `region`
  // (the header rect, in viewport coords). Handles object-fit cover/contain
  // with centred position — the common case for theme media.
  function sampleLoadedImage(entry, dispRect, region, fit) {
    if (!entry || !entry.ready || entry.failed) return null;
    var loaded = entry.img;
    var nW = loaded.naturalWidth;
    var nH = loaded.naturalHeight;
    if (!nW || !nH || dispRect.width === 0 || dispRect.height === 0) return null;

    var scale = fit === 'contain'
      ? Math.min(dispRect.width / nW, dispRect.height / nH)
      : Math.max(dispRect.width / nW, dispRect.height / nH); // cover / fill fallback
    var offX = (dispRect.width - nW * scale) / 2;
    var offY = (dispRect.height - nH * scale) / 2;

    var x0 = Math.max(region.left, dispRect.left);
    var y0 = Math.max(region.top, dispRect.top);
    var x1 = Math.min(region.right, dispRect.right);
    var y1 = Math.min(region.bottom, dispRect.bottom);
    if (x1 <= x0 || y1 <= y0) return null;

    var sx = ((x0 - dispRect.left) - offX) / scale;
    var sy = ((y0 - dispRect.top) - offY) / scale;
    var sw = (x1 - x0) / scale;
    var sh = (y1 - y0) / scale;
    sx = Math.max(0, Math.min(nW - 1, sx));
    sy = Math.max(0, Math.min(nH - 1, sy));
    sw = Math.max(1, Math.min(nW - sx, sw));
    sh = Math.max(1, Math.min(nH - sy, sh));

    ensureCanvas();
    var data;
    try {
      sampleCtx.clearRect(0, 0, SAMPLE, SAMPLE);
      sampleCtx.drawImage(loaded, sx, sy, sw, sh, 0, 0, SAMPLE, SAMPLE);
      data = sampleCtx.getImageData(0, 0, SAMPLE, SAMPLE).data;
    } catch (e) {
      entry.failed = true; // canvas tainted — give up on this image
      return null;
    }
    var sum = 0;
    var count = 0;
    for (var i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 128) continue; // skip mostly-transparent pixels
      sum += relativeLuminance(data[i], data[i + 1], data[i + 2]);
      count++;
    }
    return count ? sum / count : null;
  }

  function isHeaderNode(node) {
    if (!node || !node.closest) return true;
    if (node === spacer) return true;
    return !!(node.closest('.header-section') || node.closest('#header-group'));
  }

  // Luminance of whatever is painted behind the header at (x, y): image pixels,
  // background-image pixels, or a solid background colour — whichever we hit
  // first walking the stack from the top.
  function luminanceBehind(x, y, region) {
    var stack = document.elementsFromPoint(x, y);
    for (var i = 0; i < stack.length; i++) {
      var node = stack[i];
      if (!node || node.nodeType !== 1 || isHeaderNode(node)) continue;

      if (node.tagName === 'IMG') {
        var lum = sampleLoadedImage(
          getCorsImage(node.currentSrc || node.src),
          node.getBoundingClientRect(),
          region,
          getComputedStyle(node).objectFit || 'cover'
        );
        if (lum != null) return lum;
      }

      var cs = getComputedStyle(node);
      var bgImg = cs.backgroundImage;
      if (bgImg && bgImg.indexOf('url(') !== -1) {
        var lum2 = sampleLoadedImage(
          getCorsImage(extractUrl(bgImg)),
          node.getBoundingClientRect(),
          region,
          cs.backgroundSize === 'contain' ? 'contain' : 'cover'
        );
        if (lum2 != null) return lum2;
      }

      var col = parseRGB(cs.backgroundColor);
      if (col && col.a >= 0.5) return relativeLuminance(col.r, col.g, col.b);
      // otherwise this element is transparent — keep going deeper
    }
    var body = parseRGB(getComputedStyle(document.body).backgroundColor);
    return body && body.a >= 0.5 ? relativeLuminance(body.r, body.g, body.b) : null;
  }

  function sampleTone() {
    if (!columns) return lastTone;
    var rect = columns.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return lastTone;

    var y = Math.round(rect.top + rect.height / 2);
    if (y <= 0 || y >= window.innerHeight) return lastTone;

    // Average several points across the header width so a wide bar spanning
    // two different backgrounds resolves to a sensible overall tone.
    var fractions = [0.12, 0.32, 0.5, 0.68, 0.88];
    var sum = 0;
    var count = 0;
    for (var f = 0; f < fractions.length; f++) {
      var x = Math.round(rect.left + rect.width * fractions[f]);
      if (x <= 0 || x >= window.innerWidth) continue;
      var lum = luminanceBehind(x, y, rect);
      if (lum != null) {
        sum += lum;
        count++;
      }
    }
    if (count === 0) return lastTone;

    var avg = sum / count;
    // Hysteresis around 0.5 so scrolling over mixed content doesn't flicker.
    if (avg < 0.42) lastTone = 'dark';
    else if (avg > 0.58) lastTone = 'light';
    return lastTone;
  }

  /* ---- layout ------------------------------------------------------------ */

  function measure() {
    if (!spacer || !header) return;
    var h = header.offsetHeight;
    if (h > 0) spacer.style.height = h + 'px';
  }

  function update() {
    rafId = null;
    if (!enabled || !headerSection) return;

    var announcementBottom = 0;
    if (announcement && announcement.getBoundingClientRect) {
      announcementBottom = Math.max(0, announcement.getBoundingClientRect().bottom);
    }
    headerSection.style.top = Math.max(PINNED_GAP, announcementBottom) + 'px';

    var scrolled = getScrollTop() > SHRINK_AT;
    header.setAttribute('data-pill-state', scrolled ? 'shrink' : 'expand');

    // Open + overlaying the hero -> force light text; otherwise sample the bg.
    if (!scrolled && overlay) {
      header.setAttribute('data-pill-tone', 'dark');
    } else {
      header.setAttribute('data-pill-tone', sampleTone());
    }
  }

  function requestUpdate() {
    if (rafId == null) rafId = requestAnimationFrame(update);
  }

  /* ---- enable / disable -------------------------------------------------- */

  function enable() {
    if (enabled || !resolveNodes()) return;
    enabled = true;
    overlay = isOverlayTemplate();

    header.classList.add('pill-header');
    if (overlay) header.setAttribute('data-pill-overlay', 'true');

    // Reserve the header's height only when NOT overlaying the hero.
    if (!overlay) {
      spacer = document.createElement('div');
      spacer.className = 'pill-header-spacer';
      spacer.setAttribute('aria-hidden', 'true');
      headerSection.parentNode.insertBefore(spacer, headerSection.nextSibling);
    }

    window.addEventListener('scroll', requestUpdate, true);
    window.addEventListener('resize', onResize, { passive: true });
    update();
    measure();
  }

  function disable() {
    if (!enabled) return;
    enabled = false;

    window.removeEventListener('scroll', requestUpdate, true);
    window.removeEventListener('resize', onResize);

    if (header) {
      header.classList.remove('pill-header');
      header.removeAttribute('data-pill-state');
      header.removeAttribute('data-pill-tone');
      header.removeAttribute('data-pill-overlay');
    }
    if (headerSection) headerSection.style.top = '';
    if (spacer && spacer.parentNode) spacer.parentNode.removeChild(spacer);
    spacer = null;
  }

  function onResize() {
    if (!enabled) return;
    measure();
    requestUpdate();
  }

  function apply() {
    // Enabled on ALL viewport sizes now. Desktop (>=990px) gets the morphing
    // glass pill; mobile (<=989px) gets a transparent overlay bar + frosted
    // drawer — both gated in CSS. The shared JS handles fixed positioning, the
    // flow spacer, and adaptive light/dark tone the same way for both.
    enable();
  }

  function init() {
    if (!resolveNodes()) return;
    apply();
    if (mql.addEventListener) mql.addEventListener('change', apply);
    else if (mql.addListener) mql.addListener(apply);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', function () {
    if (enabled) {
      measure();
      requestUpdate();
    }
  });
})();
